import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, AlertTriangle, X, HelpCircle, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const FUNNEL = [
  { key: "selected", label: "Seleccionados", color: "bg-slate-400" },
  { key: "contacted", label: "Contactados", color: "bg-blue-400" },
  { key: "in_progress", label: "En proceso", color: "bg-blue-500" },
  { key: "partial", label: "Cumplimiento parcial", color: "bg-teal-500" },
  { key: "completed", label: "100% completo", color: "bg-emerald-500" },
  { key: "ready", label: "Listos para contratación", color: "bg-emerald-600" },
];

export default function Reportes() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  const resolveAlert = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`);
      setAlerts(alerts.filter((a) => a.id !== id));
      toast.success("Alerta resuelta");
    } catch { toast.error("No se pudo resolver"); }
  };

  if (!stats) return <div className="text-slate-400">Cargando…</div>;
  const max = stats.funnel.selected || 1;

  const Metric = ({ label, value }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"><p className="text-sm text-slate-500">{label}</p><p className="text-2xl font-display font-bold text-slate-900 mt-1">{value}</p></div>
  );

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-3xl font-bold text-slate-900">Reportes</h1><p className="text-slate-500 mt-1">Efectividad del seguimiento, incorporación y alertas.</p></div>

      <Tabs defaultValue="general">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="general" data-testid="tab-reportes-general">General</TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-reportes-alertas">
            Alertas{alerts.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-4 min-w-4 px-1">{alerts.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Avance promedio" value={`${stats.avg_progress}%`} />
            <Metric label="Mensajes enviados" value={stats.messages_sent} />
            <Metric label="Tasa de respuesta" value={`${stats.response_rate}%`} />
            <Metric label="Completados" value={stats.completed} />
            <Metric label="Necesitan ayuda" value={stats.needs_help} />
            <Metric label="No continuarán" value={stats.withdraw} />
            <Metric label="Sin respuesta" value={stats.no_response} />
            <Metric label="Riesgo alto + crítico" value={stats.by_risk.HIGH + stats.by_risk.CRITICAL} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="font-display font-semibold text-slate-800 mb-5 flex items-center gap-2"><TrendingUp size={18} className="text-blue-600" /> Funnel de incorporación</h2>
            <div className="space-y-3">
              {FUNNEL.map((f) => {
                const v = stats.funnel[f.key] || 0;
                const pct = Math.round((v / max) * 100);
                return (
                  <div key={f.key}>
                    <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{f.label}</span><span className="font-semibold text-slate-800">{v}</span></div>
                    <div className="h-6 rounded-lg bg-slate-100 overflow-hidden"><div className={`h-full ${f.color} rounded-lg transition-all flex items-center justify-end pr-2`} style={{ width: `${Math.max(pct, 4)}%` }}><span className="text-[11px] text-white font-medium">{pct}%</span></div></div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="alertas" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" /> Alertas activas ({alerts.length})
            </h2>
            {alerts.length === 0 ? (
              <p className="text-sm text-slate-400 py-10 text-center">Sin alertas activas.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {alerts.map((a) => (
                  <div key={a.id} data-testid={`alert-row-${a.id}`} className="flex items-center justify-between gap-3 py-3">
                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                      {a.type === "NEEDS_HELP"
                        ? <HelpCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                        : <XCircle size={16} className="text-red-500 shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700">{a.message}</p>
                        {a.worker_name && <p className="text-xs text-slate-400">{a.worker_name}</p>}
                      </div>
                    </div>
                    <button data-testid={`resolve-alert-${a.id}`} onClick={() => resolveAlert(a.id)}
                      className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1 shrink-0">
                      <X size={14} /> Resolver
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
